// First: switches Zod off its eval-based fast path before any schema is built.
import 'noddi-web/zodJitless';
import './index.css';

import { i18n } from '@lingui/core';
import * as Sentry from '@sentry/react';
import * as ReactQuery from '@tanstack/react-query';
import { noddiAsync } from 'noddi-async';
import { logQueryCacheError, openPanelFullSessionReplay, resolveApiBaseUrl, setEnvFavicon, tracking } from 'noddi-web';
import { registerPreloadErrorReload } from 'noddi-web/bootstrapApp';
import { initObservability } from 'noddi-web/observability';
import sentryConfig from 'noddi-web/sentry';
import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import { App } from './App';

const environment = import.meta.env.MODE;

// Title is set by the brand system in App.tsx (overridden per selected brand), so none is set here.
setEnvFavicon();

registerPreloadErrorReload();

// Faro RUM first so its global error/console instrumentation is in place before Sentry.
initObservability('navio-customer', __SOURCE_VERSION__);

// OpenPanel product analytics (parallel to Faro). Brand is set as an OpenPanel
// group from the brand store so events segment by brand. Not-ready when the env
// vars are unset, so this is a no-op until the customer client id is configured.
tracking.initOpenPanel({
  clientId: import.meta.env.VITE_APP_OPENPANEL_CLIENT_ID ?? '',
  apiUrl: import.meta.env.VITE_APP_OPENPANEL_API_URL ?? '',
  // Dashboard project page, so Sentry errors can link to this session's replay.
  projectUrl: import.meta.env.VITE_APP_OPENPANEL_PROJECT_URL,
  // Auto-track clicks on data-track="eventName" (and other data-* props) for OpenPanel.
  // This is OpenPanel-only; it never affects Mixpanel or explicit tracking.track() calls.
  trackAttributes: true,
  // Outbound payment / insurance / Help Scout clicks → OpenPanel `link_out`.
  trackOutgoingLinks: true,
  // Every production session; admin stays on the shared 80% sample.
  sessionReplay: openPanelFullSessionReplay
});

// Mixpanel before first paint so TrackScreen's mount effect cannot miss it.
// Replay stays off — OpenPanel is the product recorder.
tracking.initMixpanel({
  projectToken: import.meta.env.VITE_APP_MIXPANEL_PROJECT_TOKEN ?? '',
  apiHost: import.meta.env.VITE_APP_MIXPANEL_API_HOST,
  sessionReplay: { enabled: false, sampleRate: 0.1 }
});

Sentry.init({
  ...sentryConfig,
  integrations: [
    ...sentryConfig.integrations,
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    })
  ],
  // This sets the sample rate at 10%. We may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysSessionSampleRate: environment === 'development' ? 1.0 : 0.1,
  // Session Replay
  replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

noddiAsync.init({
  reactQuery: ReactQuery,
  baseUrl: resolveApiBaseUrl(import.meta.env.VITE_APP_BE_URL ?? ''),
  source: 'customer',
  sourceVersion: __SOURCE_VERSION__,
  sourceReleasedAt: __SOURCE_RELEASED_AT__,
  sdkVersion: __NSDK_PACKAGE_VERSION__,
  // Customer shows its own translated mutation feedback, so it logs errors but raises no toasts here.
  queryCacheConfig: {
    onError: (error) => logQueryCacheError('query', error)
  },
  mutationCacheConfig: {
    onError: (error) => logQueryCacheError('mutation', error)
  }
});
// Backend labels and errors follow the language the app is showing, not the browser's.
i18n.on('change', () => noddiAsync.setHeader('Accept-Language', i18n.locale));

const root = createRoot(document.getElementById('root') as HTMLDivElement);

root.render(
  <BrowserRouter>
    <App />
  </BrowserRouter>
);
